import React, { useEffect, useState } from "react";
import "./PatrolDashboard.css";
import profilePic from "../images/profile-edit.png";
import settingsIcon from "../images/settings.svg";
import * as signalR from "@microsoft/signalr";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import alertFile from "../sound/alert.wav";

const alertSound = new Audio(alertFile);

const PatrolDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [patrolUnitName, setPatrolUnitName] = useState("");

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyAFXkcBgPlixPN43xgMC1Bx9eY2k2HpyVQ",
  });

  // -------------------------------
  // Load patrol info from localStorage
  // -------------------------------
  useEffect(() => {
    try {
      const storedPatrol = JSON.parse(localStorage.getItem("adminData"));
      console.log("LocalStorage adminData:", storedPatrol); // DEBUG
      if (storedPatrol && storedPatrol.fullName) {
        setPatrolUnitName(storedPatrol.fullName);
      } else {
        console.warn("Patrol fullName not found in localStorage");
      }
    } catch (err) {
      console.error("Error parsing adminData from localStorage:", err);
    }
  }, []);

  // -------------------------------
  // Fetch recent assigned alerts
  // -------------------------------
  useEffect(() => {
    if (!patrolUnitName) return;

    const fetchAlerts = async () => {
      try {
        const res = await fetch(
          `https://senu-app.azurewebsites.net/api/alerts/assigned?patrolUnitName=${encodeURIComponent(
            patrolUnitName
          )}`
        );
        const data = await res.json();
        console.log("Fetched alerts:", data); // DEBUG
        if (res.ok && Array.isArray(data)) {
          setAlerts(data);
        }
      } catch (err) {
        console.error("Error fetching assigned alerts:", err);
      }
    };

    fetchAlerts();
  }, [patrolUnitName]);

  // -------------------------------
  // SignalR real-time alerts
  // -------------------------------
  useEffect(() => {
    if (!patrolUnitName) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://senu-app.azurewebsites.net/alerthub")
      .withAutomaticReconnect()
      .build();

    connection
      .start()
      .then(() => {
        console.log("✅ Connected to SignalR hub");
        connection.invoke("JoinPatrolGroup", `Patrol_${patrolUnitName}`);

      })
      .catch((err) => console.error("SignalR error:", err));

    connection.on("ReceiveAlert", (alert) => {
      if (alert.patrolUnitName === patrolUnitName) {
        console.log("Received alert via SignalR:", alert);
        alertSound.play().catch(() => {});
        setAlerts((prev) => [alert, ...prev]);
      }
    });

    connection.on("ReceiveAlertResolution", (resolvedAlert) => {
      setAlerts((prev) => prev.filter((a) => a.id !== resolvedAlert.id));
    });

    return () => connection.stop();
  }, [patrolUnitName]);

  // -------------------------------
  // Handle resolving alert
  // -------------------------------
  const handleResolveAlert = async () => {
    if (!selectedAlert) return;

    const resolvedAlert = {
      ...selectedAlert,
      Type: "Resolved",
      Status: "Resolved",
    };

    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://senu-app.azurewebsites.net/alerthub")
      .build();

    await connection.start();
    await connection.invoke("BroadcastResolution", resolvedAlert);

    setAlerts((prev) => prev.filter((a) => a.id !== selectedAlert.id));
    setSelectedAlert(null);
  };

  const getDirectionsLink = (lat, lng) =>
    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div className="patrol-container">
      {/* Top Bar */}
      <div className="patrol-top-bar">
        <div className="patrol-welcome">
          <img src={profilePic} alt="Profile" className="patrol-profile-pic" />
          <div className="patrol-welcome-text">
            Welcome, {patrolUnitName || "Patrol Unit"}
          </div>
        </div>
        <img src={settingsIcon} alt="Settings" className="settings-icon" />
      </div>

      {/* Recent Alerts */}
      <div className="contact-patrol-form">
        <h3>Recent Alerts</h3>
        {alerts.length === 0 ? (
          <p>No alerts to display</p>
        ) : (
          <ul>
            {alerts.map((alert) => (
              <li
                key={alert.id}
                onClick={() => setSelectedAlert(alert)}
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    alert.type === "Cancel"
                      ? "#007BFF"
                      : alert.type === "Drone"
                      ? "#FFC107"
                      : alert.type === "Panic"
                      ? "#DC3545"
                      : "#DC3535",
                  color: "white",
                  padding: "10px",
                  marginBottom: "8px",
                  borderRadius: "6px",
                }}
              >
                {alert.message} - 📍{" "}
                {parseFloat(alert.latitude || 0).toFixed(5)},{" "}
                {parseFloat(alert.longitude || 0).toFixed(5)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Alert Details Modal */}
      {selectedAlert && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="alert-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "500px",
              padding: "20px",
              position: "relative",
              boxShadow: "0 0 10px rgba(0,0,0,0.3)",
            }}
          >
            <button
              onClick={() => setSelectedAlert(null)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                fontWeight: "bold",
                color: "#007BFF",
              }}
            >
              ✖
            </button>

            <h3 style={{ textAlign: "center", marginTop: "0" }}>Alert Details</h3>
            <p>
              <strong>Status:</strong> {selectedAlert.status}
            </p>
            <p>
              <strong>Message:</strong> {selectedAlert.message}
            </p>
            <p>
              <strong>Location:</strong>{" "}
              {parseFloat(selectedAlert.latitude || 0).toFixed(5)},{" "}
              {parseFloat(selectedAlert.longitude || 0).toFixed(5)}
            </p>

            {isLoaded && (
              <div style={{ height: "300px", width: "100%", marginTop: "15px" }}>
                <GoogleMap
                  mapContainerStyle={{ height: "100%", width: "100%" }}
                  center={{
                    lat: parseFloat(selectedAlert.latitude || 0),
                    lng: parseFloat(selectedAlert.longitude || 0),
                  }}
                  zoom={15}
                >
                  <Marker
                    position={{
                      lat: parseFloat(selectedAlert.latitude || 0),
                      lng: parseFloat(selectedAlert.longitude || 0),
                    }}
                  />
                </GoogleMap>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <a
                href={getDirectionsLink(selectedAlert.latitude, selectedAlert.longitude)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: "1",
                  padding: "10px",
                  backgroundColor: "#17a2b8",
                  color: "white",
                  textAlign: "center",
                  textDecoration: "none",
                  borderRadius: "6px",
                }}
              >
                🗺️ Get Directions
              </a>
              <button
                onClick={handleResolveAlert}
                style={{
                  flex: "1",
                  padding: "10px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                ✅ Resolve Alert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrolDashboard;
